import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { api } from '../api/client';
import { ThemeContext } from '../contexts/ThemeContext';
import { FocusTimeline, processTimelineEvents, TimelineEvent } from './FocusTimeline';
import { getDistractionColor } from '../utils/colors';

interface User {
  id: number;
  display_name: string;
  email: string;
  tier: string;
  is_admin: number;
}

const SessionLogView: React.FC<{ history: any[]; startTime: string; duration: number }> = ({ history, startTime, duration }) => {
  const startTs = new Date(startTime).getTime();
  const events = useMemo(() => processTimelineEvents(history, startTs, duration * 60 * 1000), [history, startTs, duration]);
  
  return (
    <div className="p-3 bg-light border rounded text-dark">
      <div style={{ height: '40px', marginBottom: '15px' }}>
        <FocusTimeline history={history} startTime={startTs} plannedDuration={duration * 60 * 1000} />
      </div>
      <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
        <table className="table table-sm table-striped small mb-0">
          <thead>
            <tr>
              <th style={{ width: '120px' }}>Time</th>
              <th>Status</th>
              <th style={{ width: '100px' }}>Duration</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev: TimelineEvent, i: number) => {
              const dur = Math.round((ev.endTime - ev.startTime) / 1000);
              const durStr = dur >= 60 ? `${Math.floor(dur / 60)}m ${dur % 60}s` : `${dur}s`;
              const timeStr = new Date(ev.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

              const color = getDistractionColor(ev.label);

              return (
                <tr key={i}>
                  <td className="text-muted">{timeStr}</td>
                  <td>
                    <span className="fw-bold" style={{ color }}>
                      {ev.label}
                    </span>
                  </td>
                  <td className="text-muted">{durStr}</td>
                </tr>
              );
            })}
            {events.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center text-muted">No events recorded</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdminPanel: React.FC = () => {
  const { theme } = useContext(ThemeContext);
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  
  // Detail Modal State
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailError, setDetailError] = useState<string>('');
  const [detailUserId, setDetailUserId] = useState<number | null>(null);
  const [detailProfile, setDetailProfile] = useState<any>(null);
  const [detailProviders, setDetailProviders] = useState<string[]>([]);
  const [detailCalendarLinked, setDetailCalendarLinked] = useState<boolean>(false);
  const [detailUsage, setDetailUsage] = useState<any>(null);
  const [detailAnalytics, setDetailAnalytics] = useState<any>(null);

  // Session History State
  const [sessions, setSessions] = useState<any[]>([]);
  const [sessionTotal, setSessionTotal] = useState<number>(0);
  const [sessionPage, setSessionPage] = useState<number>(1);
  const [sessionLimit] = useState<number>(20);
  const [sessionSort, setSessionSort] = useState<string>('desc');
  const [sessionFilters, setSessionFilters] = useState({
    type: '',
    status: '',
    start_date: '',
    end_date: ''
  });
  const [sessionLoading, setSessionLoading] = useState<boolean>(false);

  // Log View State
  const [expandedSessionId, setExpandedSessionId] = useState<number | null>(null);
  const [sessionLogs, setSessionLogs] = useState<Record<number, any[]>>({});
  const [logLoading, setLogLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchSessions = useCallback(async (uid: number) => {
    try {
      setSessionLoading(true);
      const params: any = {
        page: sessionPage,
        limit: sessionLimit,
        sort: sessionSort,
        ...sessionFilters
      };
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });
      
      const res = await api.get(`/api/admin/users/${uid}/sessions`, { params });
      setSessions(res.data.sessions);
      setSessionTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setSessionLoading(false);
    }
  }, [sessionPage, sessionLimit, sessionSort, sessionFilters]);

  useEffect(() => {
    if (detailUserId && detailOpen) {
      fetchSessions(detailUserId);
    }
  }, [detailUserId, detailOpen, fetchSessions]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/admin/users`);
      setUsers(res.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch users. Are you an admin?');
    } finally {
      setLoading(false);
    }
  };

  const openDetails = async (userId: number) => {
    try {
      setDetailOpen(true);
      setDetailLoading(true);
      setDetailError('');
      setDetailUserId(userId);
      setDetailAnalytics(null);
      
      // Reset Session State
      setSessionPage(1);
      setSessionFilters({ type: '', status: '', start_date: '', end_date: '' });
      setSessions([]);
      
      const profReq = api.get(`/users/${userId}/profile`);
      const linkedReq = api.get(`/auth/linked`, { params: { userId } });
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const usageReq = api.get(`/users/${userId}/usage`, { params: { timezone: tz } });
      const analyticsReq = api.get(`/api/admin/users/${userId}/analytics`, { params: { timezone: tz } });
      
      const [prof, linked, usage, analytics] = await Promise.allSettled([profReq, linkedReq, usageReq, analyticsReq]);
      
      if (prof.status === 'fulfilled') setDetailProfile(prof.value.data || null);
      else setDetailProfile(null);
      
      if (linked.status === 'fulfilled') {
        const providers = Array.isArray(linked.value.data?.providers) ? linked.value.data.providers : [];
        setDetailProviders(providers);
        setDetailCalendarLinked(!!linked.value.data?.calendarLinked);
      } else {
        setDetailProviders([]);
        setDetailCalendarLinked(false);
      }
      
      if (usage.status === 'fulfilled') setDetailUsage(usage.value.data || null);
      else setDetailUsage(null);

      if (analytics.status === 'fulfilled') setDetailAnalytics(analytics.value.data || null);
      else setDetailAnalytics(null);
      
    } catch (err: any) {
      setDetailError(err.response?.data?.message || 'Failed to load details');
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleSessionLog = async (sessionId: number) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(null);
      return;
    }

    setExpandedSessionId(sessionId);

    // If we haven't fetched this log yet (or if it's empty, we might want to retry? assume caching for now)
    if (!sessionLogs[sessionId]) {
      try {
        setLogLoading(true);
        const res = await api.get(`/session/${sessionId}/metrics`);
        const history = res.data.history || [];
        setSessionLogs(prev => ({ ...prev, [sessionId]: history }));
      } catch (err) {
        console.error('Failed to fetch log', err);
        setSessionLogs(prev => ({ ...prev, [sessionId]: [] }));
      } finally {
        setLogLoading(false);
      }
    }
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      // Format: YYYY-MM-DD HH:MM (Timezone)
      const datePart = d.toLocaleDateString('en-CA'); // YYYY-MM-DD
      const timePart = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
      const tz = d.toLocaleTimeString([], { timeZoneName: 'short' }).split(' ').pop();
      return `${datePart} ${timePart} (${tz})`;
    } catch (e) {
      return '-';
    }
  };

  const updateTier = async (userId: number, newTier: string) => {
    try {
      await api.put(`/api/admin/users/${userId}/tier`, { tier: newTier });
      setUsers(users.map(u => u.id === userId ? { ...u, tier: newTier } : u));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update tier');
    }
  };

  const updateAdmin = async (userId: number, makeAdmin: boolean) => {
    try {
      await api.put(`/api/admin/users/${userId}/admin`, { is_admin: makeAdmin });
      setUsers(users.map(u => u.id === userId ? { ...u, is_admin: makeAdmin ? 1 : 0 } : u));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update admin');
    }
  };

  const deleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user? This cannot be undone.')) return;
    try {
      await api.delete(`/api/admin/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const cleanupTestUsers = async () => {
    const pattern = window.prompt('Enter email/username pattern (SQL LIKE). Example: %@example.com', '%@example.com');
    if (pattern === null) return;
    try {
      const preview = await api.post(`/api/admin/users/cleanup/test-users`, { dryRun: true, pattern });
      const count = Number(preview.data?.count || 0);
      const proceed = window.confirm(`Matched ${count} users. Delete now? This cannot be undone.`);
      if (!proceed) return;
      const res = await api.post(`/api/admin/users/cleanup/test-users`, { dryRun: false, pattern });
      const n = Number(res.data?.deleted || 0);
      alert(`Deleted ${n} users`);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Cleanup failed');
    }
  };

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center">
        <h2 className="mb-0">Admin Panel</h2>
        <button className="btn btn-sm btn-danger" onClick={cleanupTestUsers}>Delete Test Users</button>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="table-responsive">
          <table className={`table table-striped ${theme === 'dark' ? 'table-dark' : ''}`}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Display Name</th>
                <th>Email</th>
                <th>Tier</th>
                <th>Admin</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.display_name || '-'}</td>
                  <td>{user.email || '-'}</td>
                  <td>
                    <span className={`badge bg-${user.tier === 'paid' ? 'success' : 'secondary'}`}>
                      {user.tier}
                    </span>
                  </td>
                  <td>{user.is_admin ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="btn-group btn-group-sm">
                      {user.tier === 'free' ? (
                        <button className="btn btn-outline-success" onClick={() => updateTier(user.id, 'paid')}>Make Paid</button>
                      ) : (
                        <button className="btn btn-outline-warning" onClick={() => updateTier(user.id, 'free')}>Make Free</button>
                      )}
                      {user.is_admin ? (
                        <button className="btn btn-outline-secondary" onClick={() => updateAdmin(user.id, false)}>Remove Admin</button>
                      ) : (
                        <button className="btn btn-outline-primary" onClick={() => updateAdmin(user.id, true)}>Make Admin</button>
                      )}
                      <button className="btn btn-outline-info" onClick={() => openDetails(user.id)}>Details</button>
                      <button className="btn btn-outline-danger" onClick={() => deleteUser(user.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detailOpen && (
        <>
          <div className="modal show d-block" tabIndex={-1}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">User Details {detailUserId ? `#${detailUserId}` : ''}</h5>
                  <button type="button" className="btn-close" onClick={() => setDetailOpen(false)} aria-label="Close"></button>
                </div>
                <div className="modal-body">
                  {detailError && <div className="alert alert-danger">{detailError}</div>}
                  {detailLoading ? (
                    <p>Loading…</p>
                  ) : (
                    <>
                      <div className="row g-3">
                        {/* Account Overview */}
                        <div className="col-12">
                          <div className="card">
                            <div className="card-header">Account Overview</div>
                            <div className="card-body">
                              <div className="row">
                                <div className="col-md-4">
                                  <strong>Created:</strong> {formatDateTime(detailAnalytics?.account?.createdAt)}
                                </div>
                                <div className="col-md-4">
                                  <strong>Last Login:</strong> {formatDateTime(detailAnalytics?.account?.lastLogin)}
                                </div>
                                <div className="col-md-4">
                                  <strong>Status:</strong> <span className={`badge bg-${detailAnalytics?.account?.status === 'active' ? 'success' : 'secondary'}`}>{detailAnalytics?.account?.status || '-'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="card">
                            <div className="card-header">Profile</div>
                            <div className="card-body">
                              <div className="mb-2"><strong>Display Name:</strong> {detailProfile?.display_name || '-'}</div>
                              <div className="mb-2"><strong>Email:</strong> {detailProfile?.email || '-'}</div>
                              <div className="mb-2"><strong>Tier:</strong> {detailProfile?.tier || '-'}</div>
                              <div className="mb-2"><strong>Admin:</strong> {detailProfile?.is_admin ? 'Yes' : 'No'}</div>
                              <div className="mb-2"><strong>Bio:</strong> {detailProfile?.bio || '-'}</div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="card">
                            <div className="card-header">Linked Providers</div>
                            <div className="card-body">
                              {detailProviders.length > 0 ? (
                                <div className="d-flex flex-wrap gap-2">
                                  {detailProviders.map((p) => (
                                    <span key={p} className="badge bg-primary">{p}</span>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-muted">None</div>
                              )}
                              <div className="mt-2"><strong>Calendar Linked:</strong> {detailCalendarLinked ? 'Yes' : 'No'}</div>
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="card">
                            <div className="card-header">Subscription Usage</div>
                            <div className="card-body">
                              {detailUsage ? (
                                <>
                                  <div className="mb-2"><strong>Plan:</strong> {detailUsage.tier === 'paid' ? 'Plus' : 'Free'}</div>
                                  {detailUsage.tier === 'free' && (
                                    <>
                                      <div className="mb-2"><strong>Sessions This Week:</strong> {detailUsage.sessionsThisWeek}</div>
                                      {detailUsage.weekEnd && <div className="text-muted small">Week ends on {new Date(detailUsage.weekEnd).toLocaleDateString()}</div>}
                                    </>
                                  )}
                                  {detailUsage.tier === 'paid' && <div className="text-muted">Unlimited access</div>}
                                </>
                              ) : (
                                <div className="text-muted">No usage data</div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Session History Section */}
                        <div className="col-12">
                          <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                              <span>Session History</span>
                              <div className="d-flex gap-2">
                                <select 
                                  className="form-select form-select-sm" 
                                  value={sessionFilters.type} 
                                  onChange={(e) => setSessionFilters(prev => ({ ...prev, type: e.target.value }))}
                                  style={{width: 'auto'}}
                                >
                                  <option value="">All Types</option>
                                  <option value="instant">Instant</option>
                                  <option value="booked">Booked</option>
                                </select>
                                <select 
                                  className="form-select form-select-sm" 
                                  value={sessionFilters.status} 
                                  onChange={(e) => setSessionFilters(prev => ({ ...prev, status: e.target.value }))}
                                  style={{width: 'auto'}}
                                >
                                  <option value="">All Status</option>
                                  <option value="attended">Attended</option>
                                  <option value="no-show">No-Show</option>
                                  <option value="booked">Booked</option>
                                </select>
                                <input 
                                  type="date" 
                                  className="form-control form-control-sm"
                                  value={sessionFilters.start_date}
                                  onChange={(e) => setSessionFilters(prev => ({ ...prev, start_date: e.target.value }))}
                                  style={{width: 'auto'}}
                                />
                                <input 
                                  type="date" 
                                  className="form-control form-control-sm"
                                  value={sessionFilters.end_date}
                                  onChange={(e) => setSessionFilters(prev => ({ ...prev, end_date: e.target.value }))}
                                  style={{width: 'auto'}}
                                />
                              </div>
                            </div>
                            <div className="card-body">
                                {sessionLoading ? (
                                    <div className="text-center py-3">Loading sessions...</div>
                                ) : sessions.length > 0 ? (
                                    <>
                                      <div className="table-responsive">
                                          <table className="table table-sm table-hover">
                                              <thead>
                                                  <tr>
                                                      <th style={{ cursor: 'pointer' }} onClick={() => setSessionSort(sessionSort === 'asc' ? 'desc' : 'asc')}>
                                                          Date/Time {sessionSort === 'asc' ? '↑' : '↓'}
                                                      </th>
                                                      <th>Type</th>
                                                      <th>Status</th>
                                                      <th>Duration</th>
                                                      <th>Focused %</th>
                                                      <th>Device</th>
                                                      <th>Away %</th>
                                                      <th>Phone %</th>
                                                      <th>Partial Attendance %</th>
                                                      <th>Penalties</th>
                                                      <th>Actions</th>
                                                  </tr>
                                              </thead>
                                              <tbody>
                                                  {sessions.map((sess: any) => {
                                                      const totalSec = (sess.duration || 0) * 60;
                                                      const safeDiv = (val: number) => totalSec > 0 ? Math.round((val / totalSec) * 100) : 0;
                                                      
                                                      const awayPct = safeDiv(sess.away_seconds || 0);
                                                      const phonePct = safeDiv(sess.phone_seconds || 0);
                                                      
                                                      const drowsyPct = safeDiv(sess.drowsy_seconds || 0);
                                                      const posturePct = safeDiv(sess.posture_seconds || 0);
                                                      
                                                      // Calculate Focused % dynamically using Total Focus Time (source of truth)
                                                      // This ensures that any "unaccounted" time (e.g. offline tail gaps) is treated as Partial Attendance
                                                      // FALLBACK: If total_focus_seconds is 0/null (legacy data), use percent_focused as a proxy.
                                                      let focusedPct = safeDiv(sess.total_focus_seconds || 0);
                                                      
                                                      if (!sess.total_focus_seconds && sess.percent_focused > 0) {
                                                           // Legacy Fallback: Use stored percent_focused
                                                           focusedPct = Math.round(sess.percent_focused);
                                                      }

                                                      // Calculate known penalties
                                                      const knownPenalties = awayPct + phonePct + drowsyPct + posturePct;
                                                      
                                                      // Residual is Partial Attendance
                                                      // Ensure we don't go below 0
                                                      const partialPct = Math.max(0, 100 - (focusedPct + knownPenalties));

                                                      const penalties = [];
                                                      if (drowsyPct > 0) penalties.push(`Drowsy: ${drowsyPct}%`);
                                                      if (posturePct > 0) penalties.push(`Posture: ${posturePct}%`);

                                                      return (
                                                      <React.Fragment key={sess.id}>
                                                          <tr>
                                                              <td>{formatDateTime(sess.start_time)}</td>
                                                              <td>
                                                                <span className={`badge bg-${sess.type === 'instant' ? 'info' : 'primary'}`}>
                                                                  {sess.type}
                                                                </span>
                                                              </td>
                                                              <td>
                                                                <span className={`badge bg-${
                                                                  sess.attendance === 'attended' ? 'success' : 
                                                                  sess.attendance === 'no-show' ? 'danger' : 'secondary'
                                                                }`}>
                                                                  {sess.attendance}
                                                                </span>
                                                              </td>
                                                              <td>{sess.duration}m</td>
                                                              <td>{focusedPct}%</td>
                                                              <td>-</td>
                                                              <td>{awayPct}%</td>
                                                              <td>{phonePct}%</td>
                                                              <td>{partialPct}%</td>
                                                              <td className="small">{penalties.join(', ') || '-'}</td>
                                                              <td>
                                                                  {sess.timeline_available ? (
                                                                    <button 
                                                                      className={`btn btn-sm ${expandedSessionId === sess.id ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                                                      onClick={() => toggleSessionLog(sess.id)}
                                                                      title="View Timeline Log"
                                                                    >
                                                                      {expandedSessionId === sess.id ? 'Hide Log' : 'View Log'}
                                                                    </button>
                                                                  ) : (
                                                                    <span className="text-muted small" style={{ fontStyle: 'italic', opacity: 0.7 }}>
                                                                      Log unavailable
                                                                    </span>
                                                                  )}
                                                              </td>
                                                          </tr>
                                                          {expandedSessionId === sess.id && (
                                                              <tr>
                                                                  <td colSpan={11} className="p-0 border-0">
                                                                      <div className="p-2 border-bottom bg-light text-dark">
                                                                          {logLoading && !sessionLogs[sess.id] ? (
                                                                              <div className="text-center py-3 text-muted">
                                                                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                                  Loading log data...
                                                                              </div>
                                                                          ) : sessionLogs[sess.id] && sessionLogs[sess.id].length > 0 ? (
                                                                              <SessionLogView 
                                                                                  history={sessionLogs[sess.id]} 
                                                                                  startTime={sess.start_time} 
                                                                                  duration={sess.duration} 
                                                                              />
                                                                          ) : (
                                                                              <div className="text-center py-3 text-muted">
                                                                                  <i className="bi bi-exclamation-circle me-1"></i> Log is not available or empty.
                                                                              </div>
                                                                          )}
                                                                      </div>
                                                                  </td>
                                                              </tr>
                                                          )}
                                                      </React.Fragment>
                                                  );
                                                  })}
                                              </tbody>
                                          </table>
                                      </div>
                                      <div className="d-flex justify-content-between align-items-center mt-2">
                                        <div className="small text-muted">
                                          Showing {(sessionPage - 1) * sessionLimit + 1} to {Math.min(sessionPage * sessionLimit, sessionTotal)} of {sessionTotal}
                                        </div>
                                        <div className="btn-group btn-group-sm">
                                          <button 
                                            className="btn btn-outline-secondary" 
                                            disabled={sessionPage === 1}
                                            onClick={() => setSessionPage(p => p - 1)}
                                          >
                                            Prev
                                          </button>
                                          <button 
                                            className="btn btn-outline-secondary"
                                            disabled={sessionPage * sessionLimit >= sessionTotal}
                                            onClick={() => setSessionPage(p => p + 1)}
                                          >
                                            Next
                                          </button>
                                        </div>
                                      </div>
                                    </>
                                ) : (
                                    <div className="text-muted text-center py-3">No sessions found</div>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setDetailOpen(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}
    </div>
  );
};

export default AdminPanel;
